import * as React from 'react';

import type { ScheduleBoardEmployeePerDiemAssignmentTypeVM } from 'ab-viewModels/scheduleBoardEmployeePerDiemAssignment.viewModel';

import type ScheduleBoardWorkOrderVM from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import { bemElement } from 'af-utils/bem.util';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import AssignableResourceAssignment from './AssignableResourceAssignment';
import NonAssignableResourceAssignment from './NonAssignableResourceAssignment';
import styles from './styles.module.scss';

interface OwnProps {
	itemId: number;
	maxItems: number;
	assignableWorkOrders: ScheduleBoardWorkOrderVM[];
	assignedWorkOrders: ScheduleBoardWorkOrderVM[];
	perDiemAssignments?: ScheduleBoardEmployeePerDiemAssignmentTypeVM;
	isAllowedToManagePerDiems: boolean;
	isEditable: boolean;
	showPerDiem: boolean;
	/** [workOrderId]: workOrderResourceLookupId */
	resourceLookupIds: { [workOrderId: number]: number; };
	createAssignment: (workOrderId: number) => Promise<boolean>;
	removeAssignment: (workOrderId: number, workOrderCode: string, itemId: number, resourceLookupId?: number) => void;
	updateWorkOrderEmployeePerDiem?: (workOrderId: number, employeeId: number, perDiem: boolean, amount: Nullable<string>) => void;
	isDeletable: boolean;
}

interface State {
	assignmentForms: number[];
}

type Props = OwnProps;

export default class AssignmentsModalSection extends React.PureComponent<Props, State> {
	static defaultProps: Partial<Props> = {
		assignableWorkOrders: [],
		assignedWorkOrders: [],
		perDiemAssignments: {},
		showPerDiem: true,
	};

	state: State = {
		assignmentForms: [],
	};

	addAssignmentForm = () => {
		const { assignmentForms } = this.state;
		this.setState({ assignmentForms: assignmentForms.concat(assignmentForms.length) });
	};

	onCreateAssignment = async (index: number, workOrder: ScheduleBoardWorkOrderVM) => {
		const { id } = workOrder;
		const { createAssignment } = this.props;
		const isDone = await createAssignment(id);
		if (isDone) {
			this.removeAssignmentForm(index);
		}
	};

	removeAssignmentItem = (workOrderId: number, workOrderCode: string, resourceLookupId?: number) => {
		const { removeAssignment, itemId } = this.props;
		removeAssignment(workOrderId, workOrderCode, itemId, resourceLookupId);
	};

	removeAssignmentForm = (index: number) => {
		this.setState((state: State) => ({ assignmentForms: state.assignmentForms.filter((formNumber) => formNumber !== index) }));
	};

	onPerDiemCheck = (workOrderId: number, checked: boolean, amount: string) => {
		const { updateWorkOrderEmployeePerDiem, itemId } = this.props;
		const perDiem = checked;
		const pdAmount = !!perDiem ? (!!amount ? amount : '0') : null;

		if (updateWorkOrderEmployeePerDiem) {
			updateWorkOrderEmployeePerDiem(workOrderId, itemId, perDiem, pdAmount);
		}
	};

	onPerDiemAmountChange = (workOrderId: number, amount: string) => {
		const { updateWorkOrderEmployeePerDiem, itemId } = this.props;

		if (updateWorkOrderEmployeePerDiem) {
			updateWorkOrderEmployeePerDiem(workOrderId, itemId, true, amount);
		}
	};

	renderAssignment = (assignmentNumber: number, index: number) => {
		const { assignableWorkOrders } = this.props;
		return (
			<AssignableResourceAssignment
				assignmentNumber={assignmentNumber}
				createAssignment={this.onCreateAssignment}
				key={index}
				removeAssignment={this.removeAssignmentForm}
				workOrders={assignableWorkOrders}
			/>
		);
	};

	renderNonEditableAssignment = (workOrder: ScheduleBoardWorkOrderVM, index: number) => {
		const { showPerDiem, perDiemAssignments, isEditable, isDeletable, resourceLookupIds } = this.props;

		if (!perDiemAssignments) {
			return null;
		}

		return (
			<React.Fragment key={workOrder.code}>
				<NonAssignableResourceAssignment
					index={index}
					isDeletable={isDeletable && workOrder.status !== WorkOrderStatus.LOCKED}
					isEditable={isEditable && workOrder.status !== WorkOrderStatus.LOCKED}
					key={index}
					onPerDiemAmountChange={this.onPerDiemAmountChange}
					onPerDiemClick={this.onPerDiemCheck}
					perDiemAssignments={perDiemAssignments}
					removeAssignment={this.removeAssignmentItem}
					resourceLookupId={resourceLookupIds?.[workOrder.id]}
					showPerDiem={showPerDiem}
					workOrder={workOrder}
				/>
			</React.Fragment>
		);
	};

	render() {
		const { assignedWorkOrders, maxItems, isEditable, assignableWorkOrders } = this.props;
		const { assignmentForms } = this.state;

		const allowAssignmentAddition = (!assignedWorkOrders || assignedWorkOrders.length < maxItems) && isEditable && !!assignableWorkOrders.length;

		const containerClassName = bemElement(
			'schedule-board-modal__resource',
			'secondary-row',
			{
				'padded-bottom': !allowAssignmentAddition,
				'final': true,
			}
		);

		return (
			<>
				<div className={containerClassName}>
					<div className="m-b-m p-t-m">
						<SegmentLabel
							label="Today's assignments"
							tooltip="Work Orders this resource is assigned to on this day."
						/>
					</div>
					<div className={styles.assignmentsList}>
						{assignedWorkOrders.map(this.renderNonEditableAssignment)}
						{assignmentForms.map(this.renderAssignment)}
						{allowAssignmentAddition &&
							<Button
								icon="ActionPlusSmall"
								label="Add Assignment"
								onClick={this.addAssignmentForm}
								style="link"
							/>
						}
					</div>
				</div>
			</>
		);
	}
}
